/**
 * GovernmentHome — /government
 *
 * Landing page: hero, browse-by-level chips, featured departments,
 * recent public notices, value grid.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Landmark, Building2, Bell, ArrowRight, PlusCircle, MessageSquare,
  MapPin, ShieldCheck, Sparkles, Video, Users, FileText,
} from "lucide-react";
import { http } from "@/lib/api";
import GovernmentCategoryNav from "@/components/GovernmentCategoryNav";
import TopicNewsStrip from "@/components/TopicNewsStrip";
import { useLocality } from "@/contexts/LocalityContext";

export default function GovernmentHome() {
  const [home, setHome] = useState({ featured_departments: [], recent_notices: [], level_counts: [] });
  const [meta, setMeta] = useState(null);
  const [localDepts, setLocalDepts] = useState([]);
  const { locality } = useLocality();

  useEffect(() => {
    (async () => {
      const [h, m] = await Promise.all([
        http.get("/government/home").then(r => r.data).catch(() => ({})),
        http.get("/government/meta").then(r => r.data).catch(() => null),
      ]);
      setHome({ featured_departments: [], recent_notices: [], level_counts: [], ...h });
      setMeta(m);
    })();
  }, []);

  useEffect(() => {
    if (!locality?.country && !locality?.city) { setLocalDepts([]); return; }
    const params = { limit: 6 };
    if (locality?.country) params.country = locality.country;
    if (locality?.city) params.city = locality.city;
    http.get("/government/departments", { params })
      .then(r => setLocalDepts(r.data?.items || []))
      .catch(() => setLocalDepts([]));
  }, [locality?.country, locality?.state, locality?.city]);

  const levelLabel = (slug) => meta?.levels?.find(l => l.slug === slug)?.label || slug;
  const noticeLabel = (slug) => meta?.notice_kinds?.find(l => l.slug === slug)?.label || slug;

  return (
    <>
      <GovernmentCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="government-home">
        {/* Latest Politics / Government news */}
        <TopicNewsStrip topic="Government" category="Politics" testId="government-news-strip" />

        <section className="rounded-xl p-6 md:p-10 mb-8" style={{ background: "var(--wofy-surface)" }}>
          <div className="overline mb-2">ASK YOUR GOVERNMENT</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">
            Connecting Citizens with Government
          </h1>
          <p className="max-w-2xl opacity-80">
            Find the right office. Submit questions, concerns and service requests. Stay informed about
            public notices, tenders and consultations. Social Pulse forwards your message to the correct
            department and keeps a transparent audit trail.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/government/departments" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-browse">
              <Building2 className="w-4 h-4" /> Browse departments
            </Link>
            <Link to="/government/departments/new" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hover:bg-black/5"
                  style={{ borderColor: "var(--wofy-border)" }} data-testid="cta-register-dept">
              <PlusCircle className="w-4 h-4" /> Register a department
            </Link>
            <Link to="/government/my-messages" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hover:bg-black/5"
                  style={{ borderColor: "var(--wofy-border)" }} data-testid="cta-my-messages">
              <MessageSquare className="w-4 h-4" /> My messages
            </Link>
          </div>
          <div className="mt-4 text-xs opacity-70 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            Forwarding a message does not guarantee a government response or replace official emergency or statutory channels.
          </div>
        </section>

        {(locality?.city || locality?.country) && (
          <section className="mb-10" data-testid="government-near-you">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div>
                <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
                  <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />
                  GOVERNMENT NEAR YOU · {[locality.city, locality.state, locality.country].filter(Boolean).join(", ").toUpperCase()}
                </div>
                <h2 className="font-display text-2xl">Local departments</h2>
              </div>
              <Link
                to={`/government/departments?${locality?.country ? `country=${encodeURIComponent(locality.country)}` : ""}${locality?.city ? `&city=${encodeURIComponent(locality.city)}` : ""}`}
                className="text-sm wofy-link inline-flex items-center gap-0.5"
              >
                See all local <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {localDepts.length === 0 ? (
              <div className="wofy-card !p-6 text-center opacity-80">
                <p className="mb-3">No departments listed for your area yet.</p>
                <Link to="/government/departments/new" className="wofy-btn-primary inline-flex items-center gap-1.5 text-sm">
                  <PlusCircle className="w-4 h-4" /> Register the first department
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {localDepts.slice(0, 6).map(d => (
                  <Link key={d.id} to={`/government/departments/${d.slug}`}
                        className="wofy-card !p-4 hover:shadow-md transition block"
                        data-testid={`local-dept-${d.slug}`}>
                    <div className="flex items-center gap-3 mb-2">
                      {d.logo_url ? <img src={d.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" /> :
                        <div className="w-10 h-10 rounded-full bg-black/5 grid place-items-center text-xs font-mono">
                          {(d.name || "?")[0].toUpperCase()}
                        </div>}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.name}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                          <Landmark className="w-3 h-3 inline mr-1" />{levelLabel(d.level)}
                        </div>
                      </div>
                    </div>
                    {(d.city || d.country) && (
                      <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
                        <MapPin className="w-3 h-3" />{[d.city, d.state, d.country].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Browse by level</h2>
            <Link to="/government/departments" className="text-sm wofy-link inline-flex items-center gap-0.5">
              All departments <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {(meta?.levels || []).map(l => {
              const count = home.level_counts.find(x => x.level === l.slug)?.count || 0;
              return (
                <Link key={l.slug} to={`/government/departments?level=${l.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border hover:bg-black/5 transition"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`level-${l.slug}`}>
                  <Landmark className="w-3.5 h-3.5" />{l.label}
                  {count > 0 && <span className="text-[10px] opacity-60">· {count}</span>}
                </Link>
              );
            })}
          </div>
        </section>

        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Featured departments</h2>
            <Link to="/government/departments" className="text-sm wofy-link inline-flex items-center gap-0.5">
              Browse all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {home.featured_departments.length === 0 ? (
            <div className="wofy-card !p-6 text-center opacity-70">
              No departments listed yet. <Link to="/government/departments/new" className="wofy-link">Register the first →</Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {home.featured_departments.map(d => (
                <Link key={d.id} to={`/government/departments/${d.slug}`}
                      className="wofy-card !p-4 hover:shadow-md transition block"
                      data-testid={`dept-card-${d.slug}`}>
                  <div className="flex items-center gap-3 mb-2">
                    {d.logo_url ? <img src={d.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" /> :
                      <div className="w-10 h-10 rounded-full bg-black/5 grid place-items-center text-xs font-mono">
                        {(d.name || "?")[0].toUpperCase()}
                      </div>}
                    <div className="min-w-0">
                      <div className="font-medium truncate">{d.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                        <Landmark className="w-3 h-3 inline mr-1" />{levelLabel(d.level)}
                        {d.verified && <span className="ml-1 text-green-600 inline-flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" />verified</span>}
                      </div>
                    </div>
                  </div>
                  {d.description && <p className="text-sm opacity-80 line-clamp-2">{d.description}</p>}
                  {(d.city || d.country) && (
                    <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
                      <MapPin className="w-3 h-3" />{[d.city, d.state, d.country].filter(Boolean).join(", ")}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Recent public notices</h2>
            <Link to="/government/notices" className="text-sm wofy-link inline-flex items-center gap-0.5">
              All notices <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {home.recent_notices.length === 0 ? (
            <div className="wofy-card !p-6 text-center opacity-70">No notices yet.</div>
          ) : (
            <ul className="grid gap-2">
              {home.recent_notices.map(n => (
                <li key={n.id} className="wofy-card !p-3 flex items-start gap-3" data-testid={`notice-row-${n.slug}`}>
                  <Bell className="w-4 h-4 opacity-70 mt-1" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{n.title}</div>
                    <div className="text-xs opacity-70">
                      <span className="uppercase tracking-wider">{noticeLabel(n.kind)}</span>
                      {" · "}<Link to={`/government/departments/${n.department_slug}`} className="wofy-link">{n.department_name}</Link>
                      {n.valid_from && <span className="ml-2 opacity-60">from {n.valid_from}</span>}
                    </div>
                    {n.summary && <p className="text-sm opacity-80 mt-1 line-clamp-2">{n.summary}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mb-4">
          <h2 className="font-display text-2xl mb-3">What you can do here</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Building2, title: "Register your department", body: "Free listing for federal / state / district / city / taluk / panchayat offices." },
              { icon: Users,     title: "List your officials",       body: "Photos, bios, term dates, official contact info in a single trusted directory." },
              { icon: Bell,      title: "Publish public notices",    body: "Announcements, road closures, tenders, health advisories, recruitment notices." },
              { icon: MessageSquare, title: "Answer citizens",       body: "Track submitted messages, respond, update status (received → in progress → completed)." },
              { icon: Sparkles,  title: "Get an Instant Website",    body: "One-click generate a full department website — same builder as Businesses/Schools use." },
              { icon: FileText,  title: "Audit trail (built-in)",    body: "Every status change is recorded with timestamps for full transparency." },
            ].map(({ icon: Icon, title, body }, i) => (
              <div key={i} className="wofy-card !p-4">
                <div className="w-9 h-9 rounded-lg bg-black/5 grid place-items-center mb-2"><Icon className="w-5 h-5" /></div>
                <div className="font-medium">{title}</div>
                <p className="text-sm opacity-80 mt-1">{body}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
